/**
 * PDP media gallery: expose which thumbnail is the current one.
 *
 * Mirrors the active-thumbnail state onto `aria-current="true"`. This is JS
 * rather than markup because the active thumbnail is identified only by the
 * *absence* of the `opacity-30` class, which vendor `assets/theme.js` toggles
 * from the `xSplide` store's `mounted move` handler — Liquid renders every
 * thumbnail dimmed, so a static attribute would be stale from the first slide
 * change onward.
 *
 * Covers all three strips the PDP renders (main rail, quick-view rail,
 * zoom-modal rail) plus any rebuilt by `variant-select.js`, which swaps
 * `x-thumbnail` on and off nodes when a variant changes its media set. No
 * vendor file or call site changes.
 *
 * `aria-current` rather than `aria-selected` / `aria-pressed`: the rail is a
 * set of links-in-spirit to one gallery, not a tablist (no `role="tab"`) and
 * not a toggle. `true` rather than `"page"` — it selects media within the
 * current page, not a location.
 *
 * Thumbnail *names* come from `snippets/media-thumbnail-label.liquid`.
 */
(() => {
  'use strict';

  // The gallery snippet can render more than once per page (PDP plus quick
  // view); guard against a second copy double-observing every strip.
  if (window.__fbA11yMediaGallery) return;
  window.__fbA11yMediaGallery = true;

  /** Applied by Liquid to every thumbnail; removed by theme.js from the active one. */
  const DIMMED_CLASS = 'opacity-30';

  const STRIP_SELECTOR = '[id^="x-product-thumbnail-"], [id^="x-zoom-thumbnail-"]';

  const observed = new WeakSet();

  const syncThumbnail = (thumbnail) => {
    const button = thumbnail.querySelector('button');
    if (!button) return;

    if (thumbnail.classList.contains(DIMMED_CLASS)) {
      button.removeAttribute('aria-current');
    } else {
      button.setAttribute('aria-current', 'true');
    }
  };

  const syncStrip = (strip) => {
    strip.querySelectorAll('.x-thumbnail').forEach(syncThumbnail);
  };

  const watch = (strip) => {
    if (observed.has(strip)) return;
    observed.add(strip);

    syncStrip(strip);

    // Re-syncing the whole strip beats tracking which node changed: a rail holds
    // one thumbnail per media item and a slide change mutates two. Cannot loop —
    // the observer watches `class`, the sync writes `aria-current` on a
    // descendant button.
    new MutationObserver(() => syncStrip(strip)).observe(strip, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
  };

  const scan = () => document.querySelectorAll(STRIP_SELECTOR).forEach(watch);

  scan();

  // Quick-view galleries are ajax-rendered on demand, so their rails may not
  // exist at load. One delegated listener catches up on the first pointer-down
  // inside a rail; a document-wide childList observer would run on every DOM
  // write the page makes.
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('.x-thumbnail')) scan();
    },
    true
  );
})();
